import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Button,
  Alert,
} from '@mui/material';
import {
  VolumeUp,
  TrendingUp,
  TrendingDown,
  Remove as TrendingFlat,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Timeline as TimelineIcon,
  ShowChart as ChartIcon,
} from '@mui/icons-material';
import { WidgetProps } from '../types/index.js';
import { useWidgetData } from '../hooks/useWidgetData.js';
import { getApiEndpoint } from '@/config/api.js';

// Types based on the documentation specification
interface VolumeAlert {
  symbol: string;
  companyName?: string;
  currentVolume: number;
  avgVolume: number;
  volumeRatio: number;
  price: number;
  priceChange: number;
  priceChangePercent: number;
  dollarVolume: number;
  blockTrades?: Array<{
    time: string;
    size: number;
    price: number;
  }>;
  volumeProfile: {
    buyVolume: number;
    sellVolume: number;
    neutralVolume: number;
  };
  trend: 'up' | 'down' | 'neutral';
  significance: 'high' | 'medium' | 'low';
}

interface VolumeScannerData {
  volumeAlerts: VolumeAlert[];
  lastScan: string;
  totalScanned: number;
  scanDuration?: number;
}

interface VolumeScannerConfig {
  volumeMultiplier: number;
  timeframe: '1min' | '5min' | '15min' | '1hour';
  priceRange?: { min: number; max: number };
  minDollarVolume?: number;
  includeBlockTrades?: boolean;
  alertOnSpikes?: boolean;
  autoRefresh?: boolean;
  refreshInterval?: number; // seconds
}

// Real API function to fetch volume scanner data
const fetchVolumeScannerData = async (config: VolumeScannerConfig): Promise<VolumeScannerData> => {
  const params = new URLSearchParams({
    volumeMultiplier: config.volumeMultiplier.toString(),
    timeframe: config.timeframe,
    minDollarVolume: (config.minDollarVolume ?? 1000000).toString(),
    includeBlockTrades: config.includeBlockTrades?.toString() ?? 'false',
    alertOnSpikes: (config.alertOnSpikes ?? true).toString(),
    limit: '20' // Fetch more than needed for better filtering
  });

  const response = await fetch(`${getApiEndpoint()}/volumeScanner?${params}`);
  
  if (!response.ok) {
    throw new Error(`Volume scanner API error: ${response.status}`);
  }
  
  const result = await response.json();
  
  if (result.error) {
    throw new Error(result.error);
  }
  
  // Transform API response to match widget interface
  const apiData = result.data;
  
  const transformedAlerts: VolumeAlert[] = apiData.volumeAlerts.map((alert: any) => ({
    symbol: alert.symbol,
    companyName: `${alert.symbol} Inc.`, // API doesn't provide company names yet
    currentVolume: alert.currentVolume,
    avgVolume: alert.avgVolume,
    volumeRatio: alert.volumeRatio,
    price: alert.price,
    priceChange: alert.change,
    priceChangePercent: alert.changePercent,
    dollarVolume: alert.dollarVolume,
    blockTrades: config.includeBlockTrades ? [{
      time: new Date(alert.timestamp).toISOString(),
      size: Math.floor(alert.currentVolume * 0.1), // Estimate block trade size
      price: alert.price,
    }] : undefined,
    volumeProfile: {
      buyVolume: Math.floor(alert.currentVolume * 0.6), // Estimated buy volume
      sellVolume: Math.floor(alert.currentVolume * 0.35), // Estimated sell volume
      neutralVolume: Math.floor(alert.currentVolume * 0.05), // Estimated neutral volume
    },
    trend: alert.changePercent > 2 ? 'up' : alert.changePercent < -2 ? 'down' : 'neutral',
    significance: alert.confidence > 80 ? 'high' : alert.confidence > 60 ? 'medium' : 'low',
  }));
  
  return {
    volumeAlerts: transformedAlerts,
    lastScan: apiData.lastScan,
    totalScanned: apiData.totalScanned,
    scanDuration: 2.3, // Placeholder
  };
};

export const VolumeScannerWidget: React.FC<WidgetProps> = ({
  widget,
  onUpdateOutputs,
  onUpdateState,
  onUpdateConfig,
}) => {
  const [selectedAlert, setSelectedAlert] = useState<VolumeAlert | null>(null);
  const [showSettings, setShowSettings] = useState(false);

  // Get config with defaults
  const config: VolumeScannerConfig = {
    volumeMultiplier: 2,
    timeframe: '15min',
    minDollarVolume: 1000000,
    includeBlockTrades: false,
    alertOnSpikes: true,
    autoRefresh: false,
    refreshInterval: 300,
    ...widget.config,
  };

  // Fetch function for the hook
  const fetchData = useCallback(async () => {
    return await fetchVolumeScannerData(config);
  }, [
    config.volumeMultiplier,
    config.timeframe,
    config.priceRange?.min,
    config.priceRange?.max,
    config.minDollarVolume,
    config.includeBlockTrades,
  ]);

  // Use the widget data hook
  const { data, loading, error, refresh } = useWidgetData<VolumeScannerData>({
    widget,
    onUpdateState,
    fetchData,
    refreshInterval: config.autoRefresh ? (config.refreshInterval || 300) * 1000 : undefined,
    initialData: { volumeAlerts: [], lastScan: '', totalScanned: 0 },
  });

  // Auto-select first alert if none selected and we have data
  useEffect(() => {
    if (!selectedAlert && data && data.volumeAlerts.length > 0) {
      setSelectedAlert(data.volumeAlerts[0]);
    }
  }, [data?.volumeAlerts.length, selectedAlert]);

  // Update outputs when selection changes
  const lastOutputRef = useRef<any>(null);
  
  useEffect(() => {
    const outputs = {
      selectedAlert,
      volumeAlerts: data?.volumeAlerts || [],
      scanResults: data,
    };
    
    // Only update if output actually changed
    if (JSON.stringify(outputs) !== JSON.stringify(lastOutputRef.current)) {
      lastOutputRef.current = outputs;
      onUpdateOutputs(outputs);
    }
  }, [selectedAlert, data, onUpdateOutputs]);

  // Handle alert selection
  const handleSelectAlert = (alert: VolumeAlert) => {
    setSelectedAlert(alert);
  };

  // Handle config updates
  const handleConfigUpdate = (updates: Partial<VolumeScannerConfig>) => {
    onUpdateConfig({ ...config, ...updates });
  };

  const formatVolume = (volume: number) => {
    if (volume >= 1e9) return `${(volume / 1e9).toFixed(1)}B`;
    if (volume >= 1e6) return `${(volume / 1e6).toFixed(1)}M`;
    if (volume >= 1e3) return `${(volume / 1e3).toFixed(1)}K`;
    return volume.toString();
  };

  const formatCurrency = (amount: number) => {
    if (amount >= 1e9) return `$${(amount / 1e9).toFixed(1)}B`;
    if (amount >= 1e6) return `$${(amount / 1e6).toFixed(1)}M`;
    if (amount >= 1e3) return `$${(amount / 1e3).toFixed(1)}K`;
    return `$${amount.toFixed(0)}`;
  };

  const getTrendIcon = (trend: 'up' | 'down' | 'neutral') => {
    if (trend === 'up') return <TrendingUp />;
    if (trend === 'down') return <TrendingDown />;
    return <TrendingFlat />;
  };

  const getSignificanceColor = (significance: 'high' | 'medium' | 'low') => {
    switch (significance) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  if (showSettings) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">Volume Scanner Settings</Typography>
          <IconButton onClick={() => setShowSettings(false)}>
            <SettingsIcon />
          </IconButton>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Volume Multiplier"
              type="number"
              value={config.volumeMultiplier}
              onChange={(e) => handleConfigUpdate({ volumeMultiplier: parseFloat(e.target.value) || 2 })}
              InputProps={{ inputProps: { min: 1, max: 10, step: 0.1 } }}
              helperText="Minimum volume vs average (e.g., 2 = 200% of average)"
              fullWidth
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Timeframe</InputLabel>
              <Select
                value={config.timeframe}
                label="Timeframe"
                onChange={(e) => handleConfigUpdate({ timeframe: e.target.value as any })}
              >
                <MenuItem value="1min">1 Minute</MenuItem>
                <MenuItem value="5min">5 Minutes</MenuItem>
                <MenuItem value="15min">15 Minutes</MenuItem>
                <MenuItem value="1hour">1 Hour</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Min Dollar Volume"
              type="number"
              value={config.minDollarVolume || ''}
              onChange={(e) => handleConfigUpdate({ minDollarVolume: parseInt(e.target.value) || undefined })}
              helperText="Minimum dollar volume traded"
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Refresh Interval (seconds)"
              type="number"
              value={config.refreshInterval || 300}
              onChange={(e) => handleConfigUpdate({ refreshInterval: parseInt(e.target.value) || 300 })}
              InputProps={{ inputProps: { min: 30, max: 3600 } }}
              disabled={!config.autoRefresh}
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.includeBlockTrades || false}
                  onChange={(e) => handleConfigUpdate({ includeBlockTrades: e.target.checked })}
                />
              }
              label="Include Block Trades"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.autoRefresh || false}
                  onChange={(e) => handleConfigUpdate({ autoRefresh: e.target.checked })}
                />
              }
              label="Auto Refresh"
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
          <Button 
            variant="contained" 
            onClick={() => { setShowSettings(false); refresh(); }}
            startIcon={<RefreshIcon />}
          >
            Apply & Scan
          </Button>
          <Button variant="outlined" onClick={() => setShowSettings(false)}>
            Cancel
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ 
        p: 1, 
        borderBottom: 1, 
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VolumeUp color="primary" />
          <Typography variant="subtitle2" color="text.secondary">
            Volume Scanner
          </Typography>
          {loading && <CircularProgress size={16} />}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" onClick={() => setShowSettings(true)} title="Settings">
            <SettingsIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={refresh} title="Refresh" disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Status Bar */}
      <Box sx={{ px: 2, py: 1, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">
          Last scan: {data?.lastScan ? new Date(data.lastScan).toLocaleTimeString() : 'Never'} | 
          Scanned: {data?.totalScanned.toLocaleString() || '0'} stocks | 
          Found: {data?.volumeAlerts.length || 0} alerts |
          Criteria: {config.volumeMultiplier}x volume, {config.timeframe}
        </Typography>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
        {/* Alerts List */}
        <Box sx={{ width: '60%', borderRight: 1, borderColor: 'divider', overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          ) : (data?.volumeAlerts || []).length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">
                No volume alerts found with current criteria
              </Typography>
              <Button
                startIcon={<RefreshIcon />}
                size="small"
                onClick={refresh}
                sx={{ mt: 1 }}
              >
                Scan Again
              </Button>
            </Box>
          ) : (
            <List dense>
              {(data?.volumeAlerts || []).map((alert, index) => (
                <ListItem
                  key={`${alert.symbol}-${index}`}
                  onClick={() => handleSelectAlert(alert)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: selectedAlert?.symbol === alert.symbol ? 'action.selected' : 'transparent',
                    borderRadius: 1,
                    mb: 0.5,
                    mx: 0.5,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            {alert.symbol}
                          </Typography>
                          <Chip
                            size="small"
                            label={`${alert.volumeRatio}x`}
                            color={getSignificanceColor(alert.significance)}
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {getTrendIcon(alert.trend)}
                          <Typography variant="subtitle2">
                            ${alert.price.toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {alert.companyName}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                          <Typography variant="caption">
                            Vol: {formatVolume(alert.currentVolume)}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            color={alert.priceChangePercent >= 0 ? 'success.main' : 'error.main'}
                          >
                            {alert.priceChangePercent >= 0 ? '+' : ''}{alert.priceChangePercent.toFixed(2)}%
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        {/* Details Panel */}
        <Box sx={{ width: '40%', overflow: 'auto' }}>
          {selectedAlert ? (
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {selectedAlert.symbol}
                </Typography>
                <Chip
                  label={selectedAlert.significance.toUpperCase()}
                  color={getSignificanceColor(selectedAlert.significance)}
                  size="small"
                />
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedAlert.companyName}
              </Typography>

              {/* Key Metrics */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Volume Analysis
                </Typography>
                
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Current Volume</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {formatVolume(selectedAlert.currentVolume)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Avg Volume</Typography>
                    <Typography variant="body2">
                      {formatVolume(selectedAlert.avgVolume)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Volume Ratio</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {selectedAlert.volumeRatio}x
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Dollar Volume</Typography>
                    <Typography variant="body2">
                      {formatCurrency(selectedAlert.dollarVolume)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Price Info */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Price Movement
                </Typography>
                
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Current Price</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      ${selectedAlert.price.toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Change</Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 'bold',
                        color: selectedAlert.priceChangePercent >= 0 ? 'success.main' : 'error.main'
                      }}
                    >
                      {selectedAlert.priceChangePercent >= 0 ? '+' : ''}
                      {selectedAlert.priceChangePercent.toFixed(2)}%
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Volume Profile */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Volume Profile
                </Typography>
                
                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="caption">Buy Volume</Typography>
                    <Typography variant="caption" sx={{ color: 'success.main' }}>
                      {formatVolume(selectedAlert.volumeProfile.buyVolume)}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    height: 4, 
                    bgcolor: 'grey.200', 
                    borderRadius: 2,
                    overflow: 'hidden',
                    display: 'flex'
                  }}>
                    <Box 
                      sx={{ 
                        bgcolor: 'success.main', 
                        width: `${(selectedAlert.volumeProfile.buyVolume / selectedAlert.currentVolume) * 100}%`
                      }} 
                    />
                  </Box>
                </Box>

                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="caption">Sell Volume</Typography>
                    <Typography variant="caption" sx={{ color: 'error.main' }}>
                      {formatVolume(selectedAlert.volumeProfile.sellVolume)}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    height: 4, 
                    bgcolor: 'grey.200', 
                    borderRadius: 2,
                    overflow: 'hidden',
                    display: 'flex'
                  }}>
                    <Box 
                      sx={{ 
                        bgcolor: 'error.main', 
                        width: `${(selectedAlert.volumeProfile.sellVolume / selectedAlert.currentVolume) * 100}%`
                      }} 
                    />
                  </Box>
                </Box>
              </Paper>

              {/* Block Trades */}
              {selectedAlert.blockTrades && selectedAlert.blockTrades.length > 0 && (
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Recent Block Trades
                  </Typography>
                  
                  {selectedAlert.blockTrades.map((trade, index) => (
                    <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption">
                          {new Date(trade.time).toLocaleTimeString()}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                          {trade.size.toLocaleString()} @ ${trade.price.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Paper>
              )}
            </Box>
          ) : (
            <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
              <TimelineIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
              <Typography variant="body2">
                Select a volume alert to see details
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
